const failed = Symbol('fail')

const BASE_URL = '/api/data/'

const TYPES = [
  'item',
  'organization',
  'database',
  'curation',
  'curation_meta',
  'csearch',
  'label',
  'code',
  'page',
  'content',
  'field',
  'tag',
  'provider',
]

export type DataType =
  | 'item'
  | 'organization'
  | 'database'
  | 'curation'
  | 'curation_meta'
  | 'csearch'
  | 'label'
  | 'code'
  | 'page'
  | 'content'
  | 'field'
  | 'tag'
  | 'provider'

export const useDataStore = defineStore('data', () => {
  const searching = ref(false)
  const itemStore: { [id: string]: Item | typeof failed | null } = reactive({})
  const databaseStore: { [id: string]: Database | typeof failed | null } =
    reactive({})
  const organizationStore: {
    [id: string]: Organization | typeof failed | null
  } = reactive({})
  const curationStore: { [id: string]: Curation | typeof failed | null } =
    reactive({})
  const curation_metaStore: {
    [id: string]: Curation | typeof failed | null
  } = reactive({})
  const csearchStore: {
    [id: string]: CustomSearchDefinition | typeof failed | null
  } = reactive({})
  csearchStore[systemCrossSearchCs().id] = systemCrossSearchCs()
  csearchStore[systemGallerySearchCs().id] = systemGallerySearchCs()

  const labelStore: { [id: string]: Label | typeof failed | null } = reactive(
    {},
  )

  const { $getSystemCodes } = useNuxtApp()
  const codeStore: { [id: string]: Code | typeof failed | null } = reactive({})
  $getSystemCodes().forEach(code => {
    codeStore[code.id] = code
  })

  const pageStore: { [id: string]: Page | typeof failed | null } = reactive({})
  const contentStore: { [id: string]: Content | typeof failed | null } =
    reactive({})
  const fieldStore: { [id: string]: FieldLabel | typeof failed | null } =
    reactive({})
  const tagStore: { [id: string]: Tag | typeof failed | null } = reactive({})
  const providerStore: {
    [id: string]: Organization | typeof failed | null
  } = reactive({})

  const storeMap: {
    [key in DataType]: { [id: string]: any | typeof failed | null }
  } = {
    item: itemStore,
    organization: organizationStore,
    database: databaseStore,
    curation: curationStore,
    curation_meta: curation_metaStore,
    csearch: csearchStore,
    label: labelStore,
    code: codeStore,
    page: pageStore,
    content: contentStore,
    field: fieldStore,
    tag: tagStore,
    provider: providerStore,
  }

  const { getItem } = useItem()
  const { getOrganization } = useOrganization()
  const { getDatabase, getLabel, getLabelField } = useDatabase()
  const { getCuration } = useCuration()
  const { getCustomSearch } = useCustomSearch()
  const { getCode } = useCode()
  const { getPage } = usePage()
  const { getContent } = useContent()
  const { getTag } = useTag()

  const GETTER_FUNCTION: { [id: string]: (id) => Promise<any> } = {
    item: getItem,
    organization: getOrganization,
    database: getDatabase,
    curation: getCuration,
    curation_meta: getCuration,
    csearch: getCustomSearch,
    label: getLabel,
    code: getCode,
    page: getPage,
    content: getContent,
    field: getLabelField,
    tag: getTag,
  }

  function put(type: DataType, id: string, value: any) {
    if (value) {
      storeMap[type][id] = value
    }
  }

  async function getAsync<T>(type: string, id: string): Promise<T> {
    const store = storeMap[type]
    if (store[id]) {
      return new Promise((resolve, reject) => {
        resolve(store[id])
      })
    } else {
      return GETTER_FUNCTION[type](id).then((t) => {
        store[id] = t
        return t
      })
    }
  }

  function get<T>(type: DataType, id: string, retrieve = true): T {
    if (id) {
      const store = storeMap[type]
      if (store[id] === undefined) {
        if (retrieve && import.meta.client) {
          store[id] = null
          debouncedSync()
        }
        return null
      } else {
        if (store[id] === failed) {
          // failedを返すと、返した先で分岐が必要になるので、nullを返す
          return null
        }
        return store[id]
      }
    }
    return null
  }

  async function sync() {
    const q: DataQuery = {}
    let hasQuery = false
    TYPES.forEach(type => {
      const store = storeMap[type]
      const qlist = Object.keys(store).filter(
        (id: string) => store[id] === null || store[id] === failed,
      )
      if (qlist.length > 0) {
        hasQuery = true
        q[type] = qlist
      }
    })
    if (hasQuery) {
      if (searching.value) {
        debouncedSync()
      } else {
        await executeRetrieve(q)
      }
    }
  }
  const debouncedSync = debounce(100, sync)

  const { fetchGet, fetchPost } = useCustomFetch()

  async function executeRetrieve(q: DataQuery) {
    try {
      searching.value = true
      const chunk = await fetchPost<DataQuery, DataChunk>(
        BASE_URL + 'retrieve',
        q,
      )
      merge(chunk)
      // マージ後、見つからなかった値は失敗をmark
      TYPES.forEach(type => {
        const store = storeMap[type]
        Object.keys(store).forEach(id => {
          if (store[id] === null) {
            store[id] = failed
          }
        })
      })
    } catch (e) {
      console.error('retrieve error.', e)
    } finally {
      searching.value = false
    }
  }
  function merge(chunk: DataChunk) {
    try {
      if (chunk) {
        TYPES.forEach(t => {
          if (chunk[t]) {
            Object.entries(chunk[t]).forEach(([key, value]) => {
              storeMap[t][key] = value
            })
          }
        })
      }
    } catch (e) {
      console.error('merge error.', e)
    }
  }

  function item(id: string, retrieve: boolean = true): Item {
    return get('item', id, retrieve)
  }

  function organization(id: string): Organization {
    return get('organization', id)
  }

  function database(id: string): Database {
    return get('database', id)
  }

  function label(id: string): Label {
    return get('label', id)
  }

  function curation(id: string): Curation {
    return get('curation', id)
  }

  function curation_meta(id: string): Curation {
    return get('curation_meta', id)
  }

  function csearch(id: string): CustomSearchDefinition {
    return get('csearch', id)
  }

  function code(id: string): Code {
    return get('code', id)
  }

  function content(id: string): Content {
    return get('content', id)
  }

  function field(id: string): FieldLabel {
    return get('field', id)
  }

  function tag(id: string): Tag {
    return get('tag', id)
  }

  function provider(id: string): Organization {
    return get('provider', id)
  }

  function parentIdExists(id: string): Promise<Boolean> {
    return fetchGet(BASE_URL + 'parent/' + id + '/exists')
  }

  function childIdExists(id: string): Promise<Boolean> {
    return fetchGet(BASE_URL + 'child/' + id + '/exists')
  }

  return {
    merge,
    put,
    getAsync,
    get,
    item,
    organization,
    database,
    label,
    curation,
    curation_meta,
    csearch,
    code,
    content,
    field,
    tag,
    provider,
    parentIdExists,
    childIdExists,
  }
})
